import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { generateAnswerLogicDescription, generateGeneralComparisonRules } from './coordinateUtils';

/**
 * AnswerLogicSummary - Displays answer validation logic for coordinate grid questions
 * Shows how student answers are evaluated including tolerance, matching rules, and equivalent forms
 *
 * @param {Object} props
 * @param {Array} props.elements - The correct answer elements array
 * @param {Object} props.answerConfig - The answer configuration object with grid, tolerance, etc.
 * @param {boolean} props.compact - If true, shows a more compact view (default: false)
 * @param {Object} props.colors - Theme colors object (optional)
 * @param {string} props.mode - Display mode: "preview" (general rules only) or "full" (all details) (default: "full")
 * @param {string} props.title - Custom title for the accordion header (optional)
 * @param {Array<string>} props.allowedModes - Array of allowed element types for generating general rules (used in preview mode)
 */
const AnswerLogicSummary = ({
  elements,
  answerConfig,
  compact = false,
  colors,
  mode = 'full',
  title,
  allowedModes = [],
}) => {
  const [expanded, setExpanded] = useState(!compact);

  // Determine if we're in preview mode
  const isPreviewMode = mode === 'preview';

  // Generate the logic description (memoized to avoid recalculation on every render)
  // Only needed for full mode
  const logicDescription = useMemo(
    () => (isPreviewMode ? null : generateAnswerLogicDescription(elements, answerConfig)),
    [elements, answerConfig, isPreviewMode]
  );

  // Generate general comparison rules for preview mode
  const generalRules = useMemo(
    () => (isPreviewMode ? generateGeneralComparisonRules(answerConfig, allowedModes) : []),
    [answerConfig, allowedModes, isPreviewMode]
  );

  // Group elements by type for cleaner display (memoized) - only for full mode
  const elementsByType = useMemo(() => {
    if (isPreviewMode || !logicDescription) return {};
    const grouped = {};
    logicDescription.elements.forEach((el) => {
      const type = el.type === 'equationLine' ? 'line' : el.type;
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(el);
    });
    return grouped;
  }, [logicDescription, isPreviewMode]);

  // In preview mode, show if there are allowed modes; in full mode, show if there are elements
  if (isPreviewMode) {
    if (!allowedModes || allowedModes.length === 0) {
      return null;
    }
  } else {
    if (!elements || elements.length === 0) {
      return null;
    }
  }

  // Determine tolerance values
  const tolerance = answerConfig?.tolerance || 0.01;
  const precision = answerConfig?.precision || 2;
  const curveTolerance = tolerance * 10;

  // Check if curves are present (for showing curve tolerance)
  const hasCurves = isPreviewMode
    ? allowedModes.some((m) =>
        ['quadratic', 'absolute', 'sqrt', 'nthRoot', 'exponential', 'logarithmic', 'sine', 'cosine', 'tangent', 'polynomial2', 'polynomial3', 'polynomial4', 'polynomial5'].includes(m) ||
        m.startsWith('polynomial')
      )
    : Object.keys(elementsByType).includes('curve');

  // Determine the header title
  const headerTitle = title || (isPreviewMode ? 'How will my answer be graded?' : 'Answer Validation Logic');
  const HeaderIcon = isPreviewMode ? HelpOutlineIcon : InfoOutlinedIcon;

  const typeLabels = {
    point: 'Points',
    line: 'Lines',
    polygon: 'Polygons',
    circle: 'Circles',
    curve: 'Curves',
  };

  const baseStyles = {
    container: {
      mt: 2,
      borderRadius: 1,
      border: colors ? `1px solid ${colors.grey?.[700] || '#555'}` : '1px solid #555',
      backgroundColor: colors?.primary?.[800] || 'rgba(0,0,0,0.1)',
    },
    header: {
      color: colors?.blueAccent?.[300] || '#64b5f6',
    },
    label: {
      color: colors?.grey?.[300] || '#bbb',
    },
    chip: {
      backgroundColor: colors?.blueAccent?.[900] || 'rgba(100,181,246,0.1)',
      color: colors?.grey?.[200] || '#ddd',
      fontSize: '0.7rem',
    },
    equivalentChip: {
      backgroundColor: colors?.greenAccent?.[900] || 'rgba(76,175,80,0.1)',
      color: colors?.grey?.[200] || '#ddd',
      fontSize: '0.7rem',
    },
  };

  return (
    <Box sx={baseStyles.container}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: baseStyles.header.color }} />}
          sx={{ minHeight: compact ? 40 : 48, px: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HeaderIcon sx={{ fontSize: 18, color: baseStyles.header.color }} />
            <Typography variant="subtitle2" fontWeight="bold" sx={baseStyles.header}>
              {headerTitle}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
          {/* Tolerance/Precision Info */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ ...baseStyles.label, display: 'block', mb: 0.5 }}>
              Comparison Settings:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label={`Precision: ${precision} decimal places`}
                size="small"
                sx={baseStyles.chip}
              />
              <Chip
                label={`Tolerance: ${tolerance} units`}
                size="small"
                sx={baseStyles.chip}
              />
              {hasCurves && (
                <Chip
                  label={`Curve tolerance: ${curveTolerance} units`}
                  size="small"
                  sx={baseStyles.chip}
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 2, borderColor: colors?.grey?.[700] || '#555' }} />

          {/* Preview Mode: General Comparison Rules */}
          {isPreviewMode && generalRules.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{ ...baseStyles.label, display: 'block', mb: 1 }}
              >
                General Comparison Rules:
              </Typography>
              {generalRules.map((ruleEntry) => (
                <Box key={ruleEntry.type} sx={{ mb: 1.5, ml: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors?.grey?.[200] || '#ddd', fontWeight: 'medium', display: 'block', mb: 0.5 }}
                  >
                    {ruleEntry.label}:
                  </Typography>
                  <Box sx={{ ml: 1 }}>
                    {ruleEntry.rules.map((rule, rIdx) => (
                      <Typography
                        key={rIdx}
                        variant="caption"
                        sx={{ color: colors?.grey?.[400] || '#999', display: 'block' }}
                      >
                        • {rule}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Full Mode: Per-element-type rules */}
          {!isPreviewMode &&
            Object.entries(elementsByType).map(([type, typeElements]) => (
              <Box key={type} sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  sx={{ ...baseStyles.label, display: 'block', mb: 0.5 }}
                >
                  {typeLabels[type] || type} ({typeElements.length}):
                </Typography>

                {typeElements.map((el, idx) => (
                  <Box
                    key={el.id || idx}
                    sx={{
                      ml: 1,
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: colors?.primary?.[700] || 'rgba(0,0,0,0.05)',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: colors?.grey?.[200] || '#ddd', fontWeight: 'medium' }}
                    >
                      {el.label}
                    </Typography>

                    {/* Rules */}
                    <Box sx={{ mt: 0.5 }}>
                      {el.rules.map((rule, rIdx) => (
                        <Typography
                          key={rIdx}
                          variant="caption"
                          sx={{ color: colors?.grey?.[400] || '#999', display: 'block', ml: 1 }}
                        >
                          - {rule}
                        </Typography>
                      ))}
                    </Box>

                    {/* Equivalent Forms */}
                    {el.equivalentForms && el.equivalentForms.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: colors?.greenAccent?.[400] || '#81c784', display: 'block', mb: 0.5 }}
                        >
                          Equivalent forms accepted:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: 1 }}>
                          {el.equivalentForms.map((form, fIdx) => (
                            <Chip
                              key={fIdx}
                              label={form}
                              size="small"
                              sx={baseStyles.equivalentChip}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            ))}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AnswerLogicSummary;
